import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Euro, Users, Package, ArrowRight, Plus, Landmark, Percent, Receipt } from "lucide-react";

function fmt(amount) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(amount ?? 0);
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function CoachPayments() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [packages, setPackages] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [loadingRevenue, setLoadingRevenue] = useState(true);

  useEffect(() => {
    api.get("/students").then(r => setStudents(r.data)).catch(() => {});
    api.get("/coach-packages").then(r => {
      setPackages(r.data.packages || []);
    }).catch(() => {});
    api.get("/payments/coach-revenue")
      .then(r => setRevenue(r.data))
      .catch(() => setRevenue(null))
      .finally(() => setLoadingRevenue(false));
  }, []);

  const activePackages = packages.filter(p => p.is_active);
  const summary = revenue?.summary;

  return (
    <div data-testid="coach-payments-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Paiements</h1>
      <p className="text-slate-500 mt-1">Suivi des revenus de votre activité</p>

      <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50/50 mt-6">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Landmark className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 text-lg">Paiements centralisés MyCoaching</h3>
              <p className="text-sm text-slate-500 mt-1">
                Les paiements de vos élèves sont encaissés sur le compte Stripe de la plateforme MyCoaching.
                Les montants affichés correspondent à vos revenus nets, après commission plateforme
                {revenue?.commission_rate != null ? ` (${revenue.commission_rate} %)` : ""} et frais Stripe estimés.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {loadingRevenue ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="border-slate-100">
              <CardContent className="p-5">
                <Skeleton className="h-3 w-24 mb-2" />
                <Skeleton className="h-8 w-20" />
              </CardContent>
            </Card>
          ))
        ) : (
          <>
            <Card className="border-slate-100 border-l-4 border-l-emerald-500">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <Euro className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Revenus nets</p>
                    <p className="text-2xl font-bold text-slate-900">{fmt(summary?.total_revenue)}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {summary?.transactions_count ?? 0} paiement{(summary?.transactions_count ?? 0) > 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-100">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-slate-50 flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Encaissements bruts</p>
                    <p className="text-2xl font-bold text-slate-900">{fmt(summary?.gross_revenue)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-100">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center">
                    <Percent className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Commission plateforme</p>
                    <p className="text-2xl font-bold text-slate-900">{fmt(summary?.platform_fees)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-100">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-rose-50 flex items-center justify-center">
                    <Landmark className="w-5 h-5 text-rose-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Frais Stripe (estimés)</p>
                    <p className="text-2xl font-bold text-slate-900">{fmt(summary?.stripe_fees)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <Card className="border-slate-100">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center"><Users className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Élèves actifs</p>
                <p className="text-2xl font-bold text-slate-900">{students.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-100">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-purple-50 flex items-center justify-center"><Package className="w-5 h-5 text-purple-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Offres actives</p>
                <p className="text-2xl font-bold text-slate-900">{activePackages.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {!loadingRevenue && (revenue?.transactions?.length ?? 0) > 0 && (
        <Card className="border-slate-100 mt-6">
          <CardContent className="p-6">
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">Historique des paiements</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Élève</TableHead>
                  <TableHead>Offre</TableHead>
                  <TableHead className="text-right">Brut</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {revenue.transactions.map(tx => (
                  <TableRow key={tx.transaction_id}>
                    <TableCell className="text-slate-500 text-sm">{formatDate(tx.created_at)}</TableCell>
                    <TableCell className="font-medium">{tx.student_name}</TableCell>
                    <TableCell>{tx.package_name}</TableCell>
                    <TableCell className="text-right text-slate-500">{fmt(tx.amount)}</TableCell>
                    <TableCell className="text-right font-semibold text-emerald-700">{fmt(tx.net_revenue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-100 mt-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold text-slate-900">Offres proposées</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/coach/packages")}
              className="rounded-full text-sm gap-1.5"
            >
              Gérer les tarifs <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </div>
          {packages.length === 0 ? (
            <div className="text-center py-8">
              <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500 mb-3">Aucun forfait créé</p>
              <Button
                onClick={() => navigate("/coach/packages")}
                variant="outline"
                size="sm"
                className="rounded-full gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" /> Créer un forfait
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Offre</TableHead>
                  <TableHead>Prix</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {packages.map(pkg => (
                  <TableRow key={pkg.package_id}>
                    <TableCell className="font-medium">{pkg.name}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{pkg.amount.toFixed(2)}€</Badge>
                    </TableCell>
                    <TableCell>
                      {pkg.is_active ? (
                        <Badge variant="outline" className="border-green-300 text-green-700 bg-green-50">Actif</Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-400 border-slate-300">Inactif</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}